import {
  AccountSelector,
  Alert,
  AnimatedContentLoader,
  Badge,
  Button,
  Input,
  Prompt,
  SingleColumnLayout,
  TagPill,
  TextArea
} from '@components'
import {
  getExpenseClaimExpensePageUrl,
  getExpenseClaimTripPageUrl,
  getInvoicingPageUrl,
  getTravelAndExpensesPageUrl
} from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import {
  ExpenseClaim,
  ExpenseClaimTrip,
  approveExpenseClaim,
  cancelExpenseClaimApproval,
  deleteExpenseClaim,
  expenseClaimFieldError,
  fetchExpenseClaim,
  fetchExpenseClaimExpenses,
  fetchExpenseClaimProfile,
  fetchExpenseClaimTrips,
  recalculateExpenseClaim,
  rejectExpenseClaim,
  submitExpenseClaim,
  updateExpenseClaim,
  updateExpenseClaimLine,
  useCurrentUser
} from '@query'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { formatDate } from '@utils'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCheck } from 'react-icons/fa'
import { Link, useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ClaimSummaryAttachments } from './ClaimSummaryAttachments'
import { ClaimSummaryLinesTable, lineNeedsAccount } from './ClaimSummaryLinesTable'
import {
  AmountLines,
  Aside,
  AsideFooter,
  AsideFormActionsRow,
  AsideNote,
  CLAIM_STATUSES,
  Card,
  CardHeader,
  CardTitle,
  Columns,
  Help,
  MainColumn,
  PageContent,
  PageScroll,
  PageSubtitle,
  PageTitle,
  PageTop,
  PageTopMain,
  STATUS_BADGE,
  TextLink,
  TotalLine,
  buildExpenseListMeta,
  claimKey,
  claimantName,
  expenseClaimExpensesKey,
  expenseDisplayTitle
} from './shared'

const tripPeriod = (trip: ExpenseClaimTrip) => {
  const start = dayjs(trip.start_at)
  const end = dayjs(trip.end_at)
  return start.isSame(end, 'day')
    ? `${formatDate(start)} ${start.format('HH:mm')} – ${end.format('HH:mm')}`
    : `${formatDate(start)} ${start.format('HH:mm')} – ${formatDate(end)} ${end.format('HH:mm')}`
}

export const ClaimSummaryPage: React.FC = () => {
  const [t] = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const { claimId: claimIdParam } = useParams<{ claimId: string }>()
  const claimId = Number(claimIdParam)
  const canEdit = usePermission(PermissionSet.EXPENSE_CLAIMS_EDIT)
  const canViewInvoicing = usePermission(PermissionSet.INVOICING_VIEW)
  const { data: currentUser } = useCurrentUser()

  const {
    data: claim,
    isLoading,
    isError
  } = useQuery({
    queryKey: claimKey(slug, claimId),
    queryFn: () => fetchExpenseClaim(slug, claimId),
    enabled: Number.isInteger(claimId)
  })
  const { data: expenses = [] } = useQuery({
    queryKey: expenseClaimExpensesKey(slug, claimId),
    queryFn: () => fetchExpenseClaimExpenses(slug, claimId),
    enabled: Number.isInteger(claimId)
  })
  const { data: trips = [] } = useQuery({
    queryKey: [slug, 'expense-claim-trips', claimId],
    queryFn: () => fetchExpenseClaimTrips(slug, claimId),
    enabled: Number.isInteger(claimId)
  })

  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [bookingDate, setBookingDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [rejectReason, setRejectReason] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmSelfApprove, setConfirmSelfApprove] = useState(false)
  const [confirmReject, setConfirmReject] = useState(false)
  const [confirmCancelApproval, setConfirmCancelApproval] = useState(false)
  const [showMissingAccounts, setShowMissingAccounts] = useState(false)

  const messageInitializedForClaimId = useRef<number | null>(null)
  useEffect(() => {
    if (!claim) return
    if (messageInitializedForClaimId.current === claim.id) return
    messageInitializedForClaimId.current = claim.id
    setMessage(claim.message_to_approver ?? '')
  }, [claim?.id])

  const isOwn = !!claim && claim.claimant.id === currentUser?.id
  const isOwnDraft = isOwn && claim?.status === 'DRAFT' && canEdit
  const { data: profile, isFetched: profileFetched } = useQuery({
    queryKey: [slug, 'expense-claim-profile'],
    queryFn: () => fetchExpenseClaimProfile(slug),
    enabled: isOwn
  })

  const showBookingDate = !!claim?.accrual_based && !!claim.can_approve

  const recalculatedDraftId = useRef<number | null>(null)
  const recalculateDraft = useMutation({
    mutationFn: () => recalculateExpenseClaim(slug, claimId),
    onSuccess: next => {
      queryClient.setQueryData(claimKey(slug, claimId), next)
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claim-trips', claimId] })
      queryClient.invalidateQueries({ queryKey: expenseClaimExpensesKey(slug, claimId) })
    }
  })

  useEffect(() => {
    if (!claim || !isOwnDraft) return
    if (recalculatedDraftId.current === claim.id) return
    recalculatedDraftId.current = claim.id
    recalculateDraft.mutate()
  }, [claim?.id, isOwnDraft])

  const actionError = (actionError: unknown) =>
    setError(
      expenseClaimFieldError(actionError, 'booking_date') ??
        expenseClaimFieldError(actionError, 'detail') ??
        expenseClaimFieldError(actionError, 'reason') ??
        t('travelAndExpenses.loadError')
    )

  const refreshClaim = (next: ExpenseClaim) => {
    queryClient.setQueryData(claimKey(slug, claimId), next)
    queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
    queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims-summary'] })
    setError(null)
    setRejectReason('')
    setCancelReason('')
    setConfirmReject(false)
    setConfirmCancelApproval(false)
  }

  const save = useMutation({
    mutationFn: (payload: { message_to_approver?: string; tag_ids?: number[] }) =>
      updateExpenseClaim(slug, claimId, payload),
    onSuccess: next => {
      queryClient.setQueryData(claimKey(slug, claimId), next)
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
      setError(null)
    },
    onError: saveError =>
      setError(
        expenseClaimFieldError(saveError, 'detail') ??
          expenseClaimFieldError(saveError, 'tag_ids') ??
          t('travelAndExpenses.loadError')
      )
  })

  const submit = useMutation({
    mutationFn: async () => {
      await updateExpenseClaim(slug, claimId, { message_to_approver: message })
      return submitExpenseClaim(slug, claimId, showBookingDate ? bookingDate : undefined)
    },
    onSuccess: refreshClaim,
    onError: actionError
  })

  const approve = useMutation({
    mutationFn: () => approveExpenseClaim(slug, claimId, showBookingDate ? bookingDate : undefined),
    onSuccess: refreshClaim,
    onError: actionError
  })

  const reject = useMutation({
    mutationFn: () => rejectExpenseClaim(slug, claimId, rejectReason.trim()),
    onSuccess: refreshClaim,
    onError: actionError
  })

  const cancelApproval = useMutation({
    mutationFn: () => cancelExpenseClaimApproval(slug, claimId, cancelReason.trim()),
    onSuccess: refreshClaim,
    onError: actionError
  })

  const updateLine = useMutation({
    mutationFn: (payload: { lineId: number; account_id?: number | null; tag_ids?: number[] }) =>
      updateExpenseClaimLine(slug, claimId, payload.lineId, {
        account_id: payload.account_id,
        tag_ids: payload.tag_ids
      }),
    onSuccess: refreshClaim,
    onError: actionError
  })

  const sendClaim = () => {
    if (!profileFetched) return
    const missing = [
      !profile?.name.trim() ? t('travelAndExpenses.details.name') : null,
      !profile?.iban.trim() ? t('travelAndExpenses.details.iban') : null
    ].filter((field): field is string => field != null)
    if (missing.length > 0) {
      history.push(getTravelAndExpensesPageUrl(slug, { tab: 'details' }), {
        profileMissing: t('travelAndExpenses.summary.profileMissing', {
          fields: missing.join(', ')
        })
      })
      return
    }
    if (showBookingDate && !bookingDate) {
      setError(t('travelAndExpenses.summary.bookingDate'))
      return
    }
    if (claim?.self_approve) {
      setConfirmSelfApprove(true)
      return
    }
    submit.mutate()
  }

  const approveClaim = () => {
    if (showBookingDate && !bookingDate) {
      setError(t('travelAndExpenses.summary.bookingDate'))
      return
    }
    if (claimLines.some(lineNeedsAccount)) {
      setError(null)
      setShowMissingAccounts(true)
      return
    }
    approve.mutate()
  }

  const closeRejectPrompt = () => {
    setConfirmReject(false)
    setRejectReason('')
  }

  const closeCancelApprovalPrompt = () => {
    setConfirmCancelApproval(false)
    setCancelReason('')
  }

  const remove = useMutation({
    mutationFn: () => deleteExpenseClaim(slug, claimId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims-summary'] })
      history.push(getTravelAndExpensesPageUrl(slug))
    },
    onError: deleteError =>
      setError(expenseClaimFieldError(deleteError, 'detail') ?? t('travelAndExpenses.loadError'))
  })

  const currentStep = claim ? CLAIM_STATUSES.indexOf(claim.status) : 0
  const allowances = trips.reduce(
    (sum, trip) => sum + trip.mileage_amount + trip.per_diem_amount,
    0
  )
  const expenseTotal = expenses.reduce((sum, expense) => sum + (expense.amount ?? 0), 0)
  const isPostDraft = claim?.status !== 'DRAFT'
  const showClaimTagCard = isOwnDraft || (claim?.tags.length ?? 0) > 0
  const claimLines = claim?.lines ?? []
  const showLinesTable = isPostDraft && claimLines.length > 0
  const showLinesBoxes = !isPostDraft && claimLines.length > 0
  const showApprovedMeta = claim?.status === 'APPROVED' || claim?.status === 'PAID'

  return (
    <SingleColumnLayout
      header={t('travelAndExpenses.title')}
      headerTo={getTravelAndExpensesPageUrl(slug)}
    >
      <PageScroll>
        <AnimatedContentLoader isLoading={isLoading}>
          {isError && <Alert type="error" title={t('travelAndExpenses.loadError')} />}
          {claim && (
            <PageContent>
              <PageTop>
                <PageTopMain>
                  <PageTitle>
                    {t('travelAndExpenses.summary.title', { number: claim.number })}
                  </PageTitle>
                  <PageSubtitle>
                    {[claimantName(claim), business?.name].filter(Boolean).join(' · ')}
                  </PageSubtitle>
                </PageTopMain>
                <Badge type={STATUS_BADGE[claim.status]}>
                  {t(`travelAndExpenses.status.${claim.status}`)}
                </Badge>
              </PageTop>

              <Steps aria-label={t('travelAndExpenses.summary.stepsLabel')}>
                {CLAIM_STATUSES.map((status, index) => (
                  <li key={status}>
                    <StepBar $isDone={index <= currentStep} />
                    <StepLabel>{t(`travelAndExpenses.status.${status}`)}</StepLabel>
                    <Help>{t(`travelAndExpenses.summary.stepHints.${status}`)}</Help>
                  </li>
                ))}
              </Steps>

              {claim.status === 'DRAFT' && claim.status_reason && (
                <Alert
                  type="warning"
                  title={t('travelAndExpenses.summary.rejectedAlertTitle')}
                  description={t('travelAndExpenses.summary.rejectedAlertDescription', {
                    reason: claim.status_reason
                  })}
                />
              )}
              {error && <Alert type="error" title={error} />}

              <Columns>
                <MainColumn>
                  {showClaimTagCard && (
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('travelAndExpenses.summary.claimTag')}</CardTitle>
                        {isOwnDraft ? (
                          <TagSelector
                            value={claim.tags.map(tag => tag.id)}
                            onChange={tagIds => save.mutate({ tag_ids: tagIds })}
                          />
                        ) : (
                          <TagRow>
                            {claim.tags.map(tag => (
                              <TagPill
                                key={tag.id}
                                tag={{
                                  id: tag.id,
                                  name: tag.name ?? '',
                                  color: tag.color ?? undefined
                                }}
                              />
                            ))}
                          </TagRow>
                        )}
                      </CardHeader>
                    </Card>
                  )}

                  {showLinesTable && (
                    <ClaimSummaryLinesTable
                      slug={slug}
                      claimId={claimId}
                      claim={claim}
                      lines={claimLines}
                      showHelp={isPostDraft}
                      showMissingAccounts={showMissingAccounts}
                      onUpdateLine={payload => updateLine.mutate(payload)}
                    />
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {t('travelAndExpenses.summary.trips', { count: trips.length })}
                      </CardTitle>
                      {isOwnDraft && (
                        <TextLink to={getExpenseClaimTripPageUrl(slug, claimId, 'new')}>
                          {t('travelAndExpenses.summary.addTrip')}
                        </TextLink>
                      )}
                    </CardHeader>
                    {trips.length === 0 && <Help>{t('travelAndExpenses.summary.noTrips')}</Help>}
                    {trips.map(trip => (
                      <ItemBox
                        key={trip.id}
                        to={getExpenseClaimTripPageUrl(slug, claimId, trip.id)}
                      >
                        <ItemHead>
                          <div>
                            <ItemTitle>{trip.description}</ItemTitle>
                            <Help>{tripPeriod(trip)}</Help>
                            <TagRow>
                              {(trip.tags.length > 0 ? trip.tags : claim.tags).map(tag => (
                                <TagPill
                                  key={tag.id}
                                  tag={{
                                    id: tag.id,
                                    name: tag.name ?? '',
                                    color: tag.color ?? undefined
                                  }}
                                />
                              ))}
                              {trip.tags.length === 0 && claim.tags.length > 0 && (
                                <Help>{t('travelAndExpenses.summary.fromClaim')}</Help>
                              )}
                            </TagRow>
                          </div>
                          <ItemAmount>
                            {formatEurosWithOpts(
                              trip.mileage_amount + trip.per_diem_amount + trip.expense_amount
                            )}
                          </ItemAmount>
                        </ItemHead>
                        <AmountLines>
                          <span>
                            {t('travelAndExpenses.trip.mileageLine', {
                              km: trip.distance_km.replace('.', ',')
                            })}
                          </span>
                          <span>{formatEurosWithOpts(trip.mileage_amount)}</span>
                          <span>{t('travelAndExpenses.trip.perDiemLine')}</span>
                          <span>{formatEurosWithOpts(trip.per_diem_amount)}</span>
                          <span>{t('travelAndExpenses.trip.expenseLine')}</span>
                          <span>{formatEurosWithOpts(trip.expense_amount)}</span>
                        </AmountLines>
                      </ItemBox>
                    ))}
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {t('travelAndExpenses.summary.expenses', { count: expenses.length })}
                      </CardTitle>
                      {isOwnDraft && (
                        <Button
                          type="button"
                          intent="primary"
                          isSecondary
                          onClick={() =>
                            history.push(getExpenseClaimExpensePageUrl(slug, claimId, 'new'))
                          }
                        >
                          {t('travelAndExpenses.summary.addExpense')}
                        </Button>
                      )}
                    </CardHeader>
                    {expenses.length === 0 && (
                      <Help>{t('travelAndExpenses.summary.noExpenses')}</Help>
                    )}
                    {expenses.map(expense => {
                      const meta = buildExpenseListMeta(expense, t)
                      const expenseTags = expense.tags.length > 0 ? expense.tags : claim.tags
                      return (
                        <ItemBox
                          key={expense.id}
                          to={getExpenseClaimExpensePageUrl(slug, claimId, expense.id)}
                        >
                          <ItemHead>
                            <div>
                              <ItemTitle>{expenseDisplayTitle(expense.merchant, t)}</ItemTitle>
                              {meta && <Help>{meta}</Help>}
                              {expenseTags.length > 0 && (
                                <TagRow>
                                  {expenseTags.map(tag => (
                                    <TagPill
                                      key={tag.id}
                                      tag={{
                                        id: tag.id,
                                        name: tag.name ?? '',
                                        color: tag.color ?? undefined
                                      }}
                                    />
                                  ))}
                                  {expense.tags.length === 0 && claim.tags.length > 0 && (
                                    <Help>{t('travelAndExpenses.summary.fromClaim')}</Help>
                                  )}
                                </TagRow>
                              )}
                            </div>
                            <ItemAmount>{formatEurosWithOpts(expense.amount ?? 0)}</ItemAmount>
                          </ItemHead>
                        </ItemBox>
                      )
                    })}
                  </Card>

                  {isPostDraft && (
                    <ClaimSummaryAttachments slug={slug} claimId={claimId} expenses={expenses} />
                  )}

                  {showLinesBoxes && (
                    <Card>
                      <CardTitle>
                        {t('travelAndExpenses.summary.lines', { count: claimLines.length })}
                      </CardTitle>
                      {claimLines.map(line => (
                        <LineBox key={line.id}>
                          <ItemHead>
                            <div>
                              <ItemTitle>{line.description}</ItemTitle>
                              {line.basis && <Help>{line.basis}</Help>}
                              {line.distance_source === 'ROUTE' && (
                                <Help>{t('travelAndExpenses.summary.calculatedFromRoute')}</Help>
                              )}
                              {line.distance_source === 'MANUAL' && (
                                <Help>
                                  {t('travelAndExpenses.summary.editedManually')}
                                  {line.route_distance_km
                                    ? ` · ${t('travelAndExpenses.summary.originalKm', {
                                        km: line.route_distance_km.replace('.', ',')
                                      })}`
                                    : ''}
                                </Help>
                              )}
                            </div>
                            <ItemAmount>{formatEurosWithOpts(line.amount)}</ItemAmount>
                          </ItemHead>
                          {claim.can_approve ? (
                            <>
                              <AccountSelector
                                name={`line-account-${line.id}`}
                                label={t('travelAndExpenses.summary.account')}
                                value={line.account?.id}
                                allowCreate={false}
                                onChange={accountId =>
                                  updateLine.mutate({ lineId: line.id, account_id: accountId })
                                }
                              />
                              <TagSelector
                                value={line.tags.map(tag => tag.id)}
                                onChange={tagIds =>
                                  updateLine.mutate({ lineId: line.id, tag_ids: tagIds })
                                }
                              />
                            </>
                          ) : (
                            <>
                              <Help>
                                {line.account
                                  ? `${line.account.number} ${line.account.name}`
                                  : t('travelAndExpenses.summary.noAccount')}
                              </Help>
                              <TagRow>
                                {line.tags.map(tag => (
                                  <TagPill
                                    key={tag.id}
                                    tag={{
                                      id: tag.id,
                                      name: tag.name ?? '',
                                      color: tag.color ?? undefined
                                    }}
                                  />
                                ))}
                              </TagRow>
                            </>
                          )}
                          {line.expense_id != null && (
                            <TextLink
                              to={getExpenseClaimExpensePageUrl(slug, claimId, line.expense_id)}
                            >
                              {t('travelAndExpenses.summary.expensesLine')}
                            </TextLink>
                          )}
                        </LineBox>
                      ))}
                    </Card>
                  )}

                  <Card>
                    <CardTitle>{t('travelAndExpenses.summary.payment')}</CardTitle>
                    <InfoGrid>
                      <InfoBox>
                        <Help>{t('travelAndExpenses.summary.payee')}</Help>
                        <InfoTitle>
                          {claim.status === 'DRAFT'
                            ? claimantName(claim)
                            : claim.payee_name || claimantName(claim)}
                        </InfoTitle>
                        {(claim.status === 'DRAFT'
                          ? claim.claimant_profile?.iban
                          : claim.payee_iban) && (
                          <InfoText>
                            {claim.status === 'DRAFT'
                              ? claim.claimant_profile?.iban
                              : claim.payee_iban}
                          </InfoText>
                        )}
                        {claim.iban_changed && (
                          <Help>{t('travelAndExpenses.summary.ibanChanged')}</Help>
                        )}
                        {isOwn && (
                          <TextLink to={getTravelAndExpensesPageUrl(slug, { tab: 'details' })}>
                            {t('travelAndExpenses.summary.payeeFromDetails')}
                          </TextLink>
                        )}
                      </InfoBox>
                      {claim.status !== 'DRAFT' && (
                        <InfoBox>
                          <Help>{t('travelAndExpenses.list.columns.submittedBy')}</Help>
                          <InfoTitle>{claim.submitted_by_name || claimantName(claim)}</InfoTitle>
                          {claim.submitted_at && (
                            <InfoText>{formatDate(claim.submitted_at)}</InfoText>
                          )}
                        </InfoBox>
                      )}
                      {showApprovedMeta && (
                        <>
                          {canViewInvoicing && (
                            <InfoBox>
                              <Help>{t('travelAndExpenses.summary.invoices')}</Help>
                              {claim.purchase_invoice_id ? (
                                <TextLink
                                  to={getInvoicingPageUrl(slug, {
                                    id: claim.purchase_invoice_id,
                                    type: 'purchase'
                                  })}
                                >
                                  {t('travelAndExpenses.summary.openPurchaseInvoice')}
                                </TextLink>
                              ) : (
                                <InfoText>—</InfoText>
                              )}
                            </InfoBox>
                          )}
                          <InfoBox>
                            <Help>{t('travelAndExpenses.summary.approvedBy')}</Help>
                            {claim.approved_by?.name ? (
                              <>
                                <InfoTitle>{claim.approved_by.name}</InfoTitle>
                                {claim.approved_at && (
                                  <InfoText>{formatDate(claim.approved_at)}</InfoText>
                                )}
                              </>
                            ) : (
                              <InfoText>—</InfoText>
                            )}
                          </InfoBox>
                        </>
                      )}
                    </InfoGrid>
                    {isOwnDraft ? (
                      <TextArea
                        name="message_to_approver"
                        label={t('travelAndExpenses.summary.message')}
                        placeholder={t('travelAndExpenses.summary.messagePlaceholder')}
                        value={message}
                        onChange={event => setMessage(event.target.value)}
                      />
                    ) : (
                      <MessageFromClaimant>
                        <CardTitle as="h3">
                          {t('travelAndExpenses.summary.messageFromClaimant')}
                        </CardTitle>
                        <InfoText>{message.trim() ? message : '—'}</InfoText>
                      </MessageFromClaimant>
                    )}
                  </Card>
                </MainColumn>

                <Aside>
                  <CardTitle>{t('travelAndExpenses.summary.claimTotal')}</CardTitle>
                  <AmountLines>
                    <span>{t('travelAndExpenses.summary.allowances')}</span>
                    <span>{formatEurosWithOpts(allowances)}</span>
                    <span>{t('travelAndExpenses.summary.expensesLine')}</span>
                    <span>{formatEurosWithOpts(expenseTotal)}</span>
                  </AmountLines>
                  <TotalLine>
                    <span>{t('travelAndExpenses.summary.total')}</span>
                    <strong>{formatEurosWithOpts(claim.total_amount)}</strong>
                  </TotalLine>
                  {showBookingDate && (
                    <Input
                      name="booking_date"
                      type="date"
                      label={t('travelAndExpenses.summary.bookingDate')}
                      info={t('travelAndExpenses.summary.bookingDateHelp')}
                      value={bookingDate}
                      onChange={event => setBookingDate(event.target.value)}
                    />
                  )}
                  {claim.booking_date && !showBookingDate && (
                    <Help>
                      {t('travelAndExpenses.summary.bookingDate')}: {formatDate(claim.booking_date)}
                    </Help>
                  )}
                  {isOwnDraft && (
                    <>
                      {claim.total_amount <= 0 && (
                        <Help>{t('travelAndExpenses.summary.nothingPayable')}</Help>
                      )}
                      <AsideFormActionsRow>
                        <Button
                          type="button"
                          intent="success"
                          showSpinner={submit.isPending}
                          disabled={claim.total_amount <= 0 || submit.isPending}
                          onClick={sendClaim}
                        >
                          {t('travelAndExpenses.summary.submitForApproval')}
                        </Button>
                        <Button
                          type="button"
                          intent="danger"
                          showSpinner={remove.isPending}
                          onClick={() => setConfirmDelete(true)}
                        >
                          {t('travelAndExpenses.summary.delete')}
                        </Button>
                      </AsideFormActionsRow>
                      <AsideNote>{t('travelAndExpenses.summary.submitHelp')}</AsideNote>
                    </>
                  )}
                  {claim.can_approve && (
                    <AsideFormActionsRow>
                      <Button
                        type="button"
                        intent="success"
                        icon={<FaCheck />}
                        showSpinner={approve.isPending}
                        disabled={approve.isPending}
                        onClick={approveClaim}
                      >
                        {t('travelAndExpenses.summary.approve')}
                      </Button>
                      <Button
                        type="button"
                        intent="danger"
                        disabled={reject.isPending}
                        onClick={() => setConfirmReject(true)}
                      >
                        {t('travelAndExpenses.summary.reject')}
                      </Button>
                    </AsideFormActionsRow>
                  )}
                  {claim.can_cancel && (
                    <AsideFormActionsRow>
                      <Button
                        type="button"
                        intent="danger"
                        disabled={cancelApproval.isPending}
                        onClick={() => setConfirmCancelApproval(true)}
                      >
                        {t('travelAndExpenses.summary.cancelApproval')}
                      </Button>
                    </AsideFormActionsRow>
                  )}
                  <AsideFooter>
                    {t('travelAndExpenses.summary.updated', {
                      date: formatDate(claim.updated_at)
                    })}
                  </AsideFooter>
                </Aside>
              </Columns>
            </PageContent>
          )}
        </AnimatedContentLoader>
      </PageScroll>
      <Prompt
        isVisible={confirmDelete}
        title={t('general.areYouSure')}
        description={t('travelAndExpenses.summary.deleteConfirm')}
        onClose={() => setConfirmDelete(false)}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setConfirmDelete(false)
          },
          {
            text: t('travelAndExpenses.summary.delete'),
            intent: 'danger',
            action: () => remove.mutate(),
            showSpinner: remove.isPending,
            disabled: remove.isPending
          }
        ]}
      />
      <Prompt
        isVisible={confirmSelfApprove}
        title={t('travelAndExpenses.summary.submitForApproval')}
        description={t('travelAndExpenses.summary.selfApproveConfirm')}
        onClose={() => setConfirmSelfApprove(false)}
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setConfirmSelfApprove(false)
          },
          {
            text: t('travelAndExpenses.summary.confirm'),
            intent: 'success',
            action: () =>
              submit.mutate(undefined, { onSettled: () => setConfirmSelfApprove(false) }),
            showSpinner: submit.isPending,
            disabled: submit.isPending
          }
        ]}
      />
      <Prompt
        isVisible={confirmReject}
        title={t('travelAndExpenses.summary.reject')}
        description={
          <TextArea
            name="reject_reason"
            label={t('travelAndExpenses.summary.rejectReason')}
            value={rejectReason}
            onChange={event => setRejectReason(event.target.value)}
          />
        }
        onClose={closeRejectPrompt}
        buttons={[
          {
            text: t('general.cancel'),
            action: closeRejectPrompt
          },
          {
            text: t('travelAndExpenses.summary.reject'),
            intent: 'danger',
            action: () => reject.mutate(),
            showSpinner: reject.isPending,
            disabled: !rejectReason.trim() || reject.isPending
          }
        ]}
      />
      <Prompt
        isVisible={confirmCancelApproval}
        title={t('travelAndExpenses.summary.cancelApproval')}
        description={
          <TextArea
            name="cancel_approval_reason"
            label={t('travelAndExpenses.summary.cancelReason')}
            value={cancelReason}
            onChange={event => setCancelReason(event.target.value)}
          />
        }
        onClose={closeCancelApprovalPrompt}
        buttons={[
          {
            text: t('general.cancel'),
            action: closeCancelApprovalPrompt
          },
          {
            text: t('travelAndExpenses.summary.confirm'),
            intent: 'danger',
            action: () => cancelApproval.mutate(),
            showSpinner: cancelApproval.isPending,
            disabled: !cancelReason.trim() || cancelApproval.isPending
          }
        ]}
      />
    </SingleColumnLayout>
  )
}

const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs}rem;
  }
`

const StepBar = styled.span<{ $isDone: boolean }>`
  height: ${({ theme }) => theme.borderRadius.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.xs}rem;
  background: ${({ theme, $isDone }) =>
    $isDone ? theme.colors.primary : theme.colors.neutralGray};
`

const StepLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const LineBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const ItemBox = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  color: inherit !important;
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const ItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ItemTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const ItemAmount = styled.div`
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-variant-numeric: tabular-nums;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(${({ theme }) => theme.layout.infoGridCellMin}rem, 1fr)
  );
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
`

const InfoTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const InfoText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-variant-numeric: tabular-nums;
`

const MessageFromClaimant = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};

  h3 {
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
  }

  ${InfoText} {
    white-space: pre-wrap;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`
